import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../component/Navbar';
import TestAutocomplete from '../component/TestAutocomplete';
import './UpdateTestConfigPage.css';

const API_BASE_URL = 'https://apiv2.labridge.in';
const blankUpdate = () => ({ testId: '', testPrice: '', mrp: '', tat: '', selectedTest: null });

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || error.message || fallback;

const getResults = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

function UpdateTestConfigPage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('client-wise');
  const [mode, setMode] = useState('single');
  const [updates, setUpdates] = useState([blankUpdate()]);
  const [processingCenters, setProcessingCenters] = useState([]);
  const [selectedLabCodes, setSelectedLabCodes] = useState([]);
  const [clientEmail, setClientEmail] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [isFindingClient, setIsFindingClient] = useState(false);
  const [clientError, setClientError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState(null);

  useEffect(() => {
    const savedLogin = localStorage.getItem('loginData');
    const adminData = savedLogin ? JSON.parse(savedLogin) : null;

    if (!adminData || !Array.isArray(adminData.access) || !adminData.access.includes('9')) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/apiforsubmission/labs`)
      .then((result) => setProcessingCenters(getResults(result.data)))
      .catch((requestError) => toast.error(getErrorMessage(requestError, 'Unable to load processing centers.')));
  }, []);

  const findClient = async () => {
    const email = clientEmail.trim();
    if (!email) {
      setClientError('Enter a client email address to search.');
      return;
    }

    setIsFindingClient(true);
    setClientError('');
    setClientOptions([]);
    try {
      const result = await axios.get(`${API_BASE_URL}/admin/labs/${encodeURIComponent(email)}`);
      const clients = getResults(result.data);
      if (clients.length === 0) {
        setClientError('No client was found for this email address.');
        return;
      }
      setClientOptions(clients);
    } catch (requestError) {
      setClientError(getErrorMessage(requestError, 'Unable to find the client.'));
    } finally {
      setIsFindingClient(false);
    }
  };

  const toggleClient = (client) => {
    const clientId = String(client._id || client.id);
    setSelectedClients((currentClients) => currentClients.some((item) => String(item._id || item.id) === clientId)
      ? currentClients.filter((item) => String(item._id || item.id) !== clientId)
      : [...currentClients, client]);
  };

  const updateRow = (index, field, value) => {
    setUpdates((currentUpdates) => currentUpdates.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row
    ));
  };

  const addRow = () => setUpdates((currentUpdates) => [...currentUpdates, blankUpdate()]);
  const removeRow = (index) => setUpdates((currentUpdates) => currentUpdates.filter((_, rowIndex) => rowIndex !== index));

  const validate = () => {
    const errors = [];
    updates.forEach((row, index) => {
      const rowNumber = index + 1;
      if (!row.testId.trim()) errors.push(`Row ${rowNumber}: Test ID is required.`);
      if (!row.testPrice.trim() && !row.mrp.trim() && !row.tat.trim()) {
        errors.push(`Row ${rowNumber}: Fill at least one update field.`);
      }
      if (row.testPrice.trim() && !Number.isFinite(Number(row.testPrice))) {
        errors.push(`Row ${rowNumber}: Test price must be numeric.`);
      }
    });

    if (scope === 'client-wise' && selectedClients.length === 0) {
      errors.push('Select at least one client for Client Wise updates.');
    }
    if (scope === 'processing-center-wise' && selectedLabCodes.length === 0) {
      errors.push('Select at least one processing center.');
    }
    return errors;
  };

  const buildUpdates = () => updates.map((row) => {
    const update = { testId: row.testId.trim() };
    if (row.testPrice.trim()) update.testPrice = Number(row.testPrice);
    if (row.mrp.trim()) update.mrp = row.mrp.trim();
    if (row.tat.trim()) update.tat = row.tat.trim();
    return update;
  });

  const submit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (validationErrors.length) {
      const message = validationErrors.join(' ');
      setError(message);
      toast.error('Please correct the update form.');
      return;
    }

    const payload = { scope, updates: buildUpdates() };
    if (scope === 'client-wise') {
      payload.partnerIds = selectedClients.map((client) => String(client._id || client.id));
    }
    if (scope === 'processing-center-wise') {
      payload.labCodes = selectedLabCodes;
    }

    setIsSubmitting(true);
    setError('');
    setResponse(null);
    try {
      const result = await axios.post(`${API_BASE_URL}/admin/update-test-config`, payload);
      if (result.data?.success === false) {
        throw new Error(result.data?.message || 'Test configuration update failed.');
      }
      setResponse(result.data);
      toast.success(result.data?.message || 'Test configuration updated successfully.');
    } catch (requestError) {
      const message = getErrorMessage(requestError, 'Unable to update test configuration.');
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setUpdates([blankUpdate()]);
    setSelectedLabCodes([]);
    setSelectedClients([]);
    setClientOptions([]);
    setClientEmail('');
    setClientError('');
    setError('');
    setResponse(null);
  };

  const scopeCopy = {
    'client-wise': 'Updates only selected client records in lab_tests.',
    'processing-center-wise': 'Updates both the center master list and all client records of that center.',
    'all-centers-all-clients': 'Updates the same test everywhere.',
  };

  return (
    <div className="wrapper">
      <Navbar />
      <main className="container mt-5 update-test-config-page" id="content">
        <div className="mb-4">
          <h1 className="mb-1">Update Test Configuration</h1>
          <p className="text-muted mb-0">Change test price, MRP, or TAT without altering other test details.</p>
        </div>

        <form onSubmit={submit}>
          <section className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="update-scope">Scope</label>
                  <select id="update-scope" className="form-select" value={scope} onChange={(event) => setScope(event.target.value)} disabled={isSubmitting}>
                    <option value="client-wise">Client Wise</option>
                    <option value="processing-center-wise">Processing Center Wise</option>
                    <option value="all-centers-all-clients">All Centers All Clients</option>
                  </select>
                  <div className="form-text">{scopeCopy[scope]}</div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="update-mode">Update mode</label>
                  <select id="update-mode" className="form-select" value={mode} onChange={(event) => { const nextMode = event.target.value; setMode(nextMode); if (nextMode === 'single') setUpdates((currentUpdates) => currentUpdates.slice(0, 1)); }} disabled={isSubmitting}>
                    <option value="single">Single Test</option>
                    <option value="bulk">Bulk Update</option>
                  </select>
                </div>
              </div>

              {scope === 'client-wise' && <ClientSelector email={clientEmail} setEmail={setClientEmail} options={clientOptions} selected={selectedClients} onFind={findClient} onToggle={toggleClient} loading={isFindingClient} error={clientError} disabled={isSubmitting} />}

              {scope === 'processing-center-wise' && (
                <div className="mt-4">
                  <label className="form-label" htmlFor="update-centers">Processing centers</label>
                  <select id="update-centers" className="form-select" multiple value={selectedLabCodes} onChange={(event) => setSelectedLabCodes(Array.from(event.target.selectedOptions, (option) => option.value))} disabled={isSubmitting}>
                    {processingCenters.map((center) => <option key={center._id || center.labCode} value={center.labCode}>{center.labName || center.name || center.labCode} ({center.labCode})</option>)}
                  </select>
                  <div className="form-text">Hold Ctrl (Windows) or Command (Mac) to select multiple centers.</div>
                </div>
              )}
            </div>
          </section>

          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <section className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                <div><h2 className="h4 mb-1">{mode === 'bulk' ? 'Update rows' : 'Test update'}</h2><p className="text-muted mb-0">A row must contain a test ID and at least one field to update.</p></div>
                {mode === 'bulk' && <button type="button" className="btn btn-outline-primary" onClick={addRow} disabled={isSubmitting}>Add row</button>}
              </div>
              {updates.map((row, index) => <UpdateRow key={index} index={index} row={row} scope={scope} clientId={selectedClients[0]?._id || selectedClients[0]?.id} labCodes={scope === 'processing-center-wise' ? selectedLabCodes : []} onChange={updateRow} onRemove={removeRow} removable={mode === 'bulk' && updates.length > 1} disabled={isSubmitting} />)}
            </div>
          </section>

          <div className="d-flex gap-2 flex-wrap mb-4">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Updating…' : mode === 'bulk' ? 'Update all rows' : 'Update test configuration'}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={reset} disabled={isSubmitting}>Reset form</button>
          </div>
        </form>

        {response && <UpdateResultsTable response={response} />}
      </main>
      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
}

function ClientSelector({ email, setEmail, options, selected, onFind, onToggle, loading, error, disabled }) {
  return <div className="mt-4"><label className="form-label" htmlFor="update-client-email">Clients</label><div className="d-flex gap-2 flex-wrap"><input id="update-client-email" type="email" className="form-control update-test-client-search" value={email} onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); onFind(); } }} placeholder="Enter client email address" disabled={disabled} /><button type="button" className="btn btn-outline-primary" onClick={onFind} disabled={disabled || loading}>{loading ? 'Searching…' : 'Find client'}</button></div><div className="form-text">Search by registered email, then select one or more clients.</div>{error && <div className="text-danger small mt-1">{error}</div>}{options.length > 0 && <div className="update-test-options mt-2">{options.map((client) => { const id = String(client._id || client.id); const isSelected = selected.some((item) => String(item._id || item.id) === id); return <label className="update-test-option" key={id}><input type="checkbox" className="form-check-input me-2" checked={isSelected} onChange={() => onToggle(client)} disabled={disabled} />{client.labName || client.name || id}</label>; })}</div>}{selected.length > 0 && <div className="update-test-chips mt-2">{selected.map((client) => <span className="badge text-bg-primary update-test-chip" key={client._id || client.id}>{client.labName || client.name || client._id}<button type="button" onClick={() => onToggle(client)} disabled={disabled} aria-label={`Remove ${client.labName || client.name}`}>×</button></span>)}</div>}</div>;
}

function UpdateRow({ index, row, scope, clientId, labCodes, onChange, onRemove, removable, disabled }) {
  const setValue = (field) => (event) => onChange(index, field, event.target.value);
  return <fieldset className="update-test-row"><legend className="visually-hidden">Update row {index + 1}</legend><div className="d-flex justify-content-between align-items-center mb-3"><h3 className="h5 mb-0">{`Test ${index + 1}`}</h3>{removable && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(index)} disabled={disabled}>Remove row</button>}</div><div className="mb-3"><TestAutocomplete scope={scope} clientId={clientId} labCodes={labCodes} onSelect={(selectedTest) => onChange(index, 'selectedTest', selectedTest) || onChange(index, 'testId', String(selectedTest.testId || ''))} disabled={disabled} label="Find test to update" /></div><div className="row g-3"><UpdateField label="Test ID *"><input className="form-control" value={row.testId} onChange={setValue('testId')} disabled={disabled} /></UpdateField><UpdateField label="Test price"><input type="number" min="0" step="0.01" className="form-control" value={row.testPrice} onChange={setValue('testPrice')} disabled={disabled} /></UpdateField><UpdateField label="MRP"><input className="form-control" value={row.mrp} onChange={setValue('mrp')} disabled={disabled} /></UpdateField><UpdateField label="TAT"><input className="form-control" value={row.tat} onChange={setValue('tat')} disabled={disabled} /></UpdateField></div></fieldset>;
}

function UpdateField({ label, children }) { return <div className="col-12 col-md-6 col-lg-3"><label className="form-label">{label}</label>{children}</div>; }

function UpdateResultsTable({ response }) {
  const issues = [
    ['Missing partner IDs', response.missingPartnerIds],
    ['Missing partner-lab mappings', response.missingPartnerLabMappings],
    ['Missing processing center IDs', response.requestedProcessingCenterIssues?.missingProcessingLabIds],
    ['Missing processing center codes', response.requestedProcessingCenterIssues?.missingLabCodes],
  ].filter(([, value]) => Array.isArray(value) && value.length > 0);

  return <section className="card mb-4" aria-labelledby="update-results-heading"><div className="card-body"><h2 id="update-results-heading" className="h4">Update results</h2><div className="row g-3 mb-4"><ResultStat label="Scope" value={response.scope} /><ResultStat label="Updates requested" value={response.totalUpdatesRequested} /></div><div className="table-responsive"><table className="table table-striped align-middle"><thead><tr><th>Test ID</th><th>Updated fields</th><th>Lab tests matched</th><th>Lab tests modified</th><th>Master records matched</th><th>Master records modified</th></tr></thead><tbody>{(response.results || []).map((result) => <tr key={result.testId}><td>{result.testId}</td><td>{(result.updatedFields || []).join(', ') || '-'}</td><td>{result.labTestsMatched ?? 0}</td><td>{result.labTestsModified ?? 0}</td><td>{result.testlistBasicsMatched ?? 0}</td><td>{result.testlistBasicsModified ?? 0}</td></tr>)}</tbody></table></div>{issues.length > 0 && <div className="mt-3"><h3 className="h5">Scope issues</h3>{issues.map(([label, value]) => <details className="update-test-details" key={label}><summary>{label} ({value.length})</summary><pre>{JSON.stringify(value, null, 2)}</pre></details>)}</div>}</div></section>;
}

function ResultStat({ label, value }) { return <div className="col-6 col-md-3"><div className="update-test-stat-label">{label}</div><strong>{value ?? '-'}</strong></div>; }

export default UpdateTestConfigPage;
